import * as React from "react"
import { useState, useCallback, useEffect } from "react"
import { Toaster } from "react-hot-toast"
import { InvoiceProvider, useInvoice } from "@/contexts/InvoiceContext"
import { useInvoiceOperations } from "@/hooks/useInvoiceOperations"
import { Invoice, EmailTemplate } from "@/types/invoice"
import { Dropzone } from "@/components/ui/dropzone"
import { Button } from "@/components/ui/button"
import { Archive } from "lucide-react"
import { AnalyticsDashboard } from "@/components/AnalyticsDashboard"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { EmailTemplateEditor } from "@/components/EmailTemplateEditor"
import { EmailPreview } from "@/components/EmailPreview"
import { ArchiveDialog } from "@/components/ArchiveDialog"
import { ArchiveHistory } from "@/components/ArchiveHistory"

function AppContent() {
  const { state } = useInvoice()
  const { parseFile, parseText, loadInvoices, loadEmailTemplates, generateEmailContent, openEmail, archiveInvoice, restoreInvoice, isLoading, error } = useInvoiceOperations()
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>(state.invoices)
  const [activeTab, setActiveTab] = useState<'file' | 'text'>('text')
  const [textInput, setTextInput] = useState("")
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [archiveInvoiceData, setArchiveInvoiceData] = useState<Invoice | null>(null)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)

  const handleFileUpload = useCallback(async (files: File[]) => {
    await parseFile(files)
  }, [parseFile])

  const handleTextSubmit = useCallback(async (text: string) => {
    console.log("App handleTextSubmit called", { text })
    await parseText(text)
  }, [parseText])

  const handleFilteredInvoices = useCallback((invoices: Invoice[]) => {
    setFilteredInvoices(invoices)
  }, [])

  const handlePreviewEmail = useCallback((invoice: Invoice) => {
    setPreviewInvoice(invoice)
    setIsPreviewOpen(true)
  }, [])

  const handleOpenEmail = useCallback((invoice: Invoice) => {
    openEmail(invoice, state.emailTemplate)
  }, [openEmail, state.emailTemplate])

  const handlePreviewTemplate = useCallback((template: EmailTemplate) => {
    if (previewInvoice) {
      // Update the preview with the new template
      setIsPreviewOpen(false)
      setTimeout(() => {
        setPreviewInvoice(previewInvoice)
        setIsPreviewOpen(true)
      }, 100)
    }
  }, [previewInvoice])

  const handleArchiveInvoice = useCallback((invoice: Invoice) => {
    setArchiveInvoiceData(invoice)
    setIsArchiveOpen(true)
  }, [])

  const handleArchiveConfirm = useCallback(async (notes?: string) => {
    if (archiveInvoiceData) {
      await archiveInvoice(archiveInvoiceData, state.emailTemplate, notes)
    }
  }, [archiveInvoiceData, archiveInvoice, state.emailTemplate])

  // Load invoices and email templates from database on component mount
  useEffect(() => {
    loadInvoices()
    loadEmailTemplates()
  }, []) // Empty dependency array - only run once on mount

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold">Invoice Parser & Analytics</h1>
            <p className="text-xl text-muted-foreground">
              Upload invoice files and analyze overdue invoices
            </p>
          </div>

          {/* Input Options */}
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">Add Invoice Data</h2>
            
            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-muted p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('file')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'file'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Upload File
              </button>
              <button
                onClick={() => setActiveTab('text')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'text'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Paste Text
              </button>
            </div>

            {/* Tab Content */}
            <div className="mt-4">
              {activeTab === 'file' ? (
                <div className="space-y-4">
                  <Dropzone onDrop={handleFileUpload} />
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Paste Invoice Text</h3>
                  <textarea
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    className="w-full min-h-[200px] p-4 border rounded-lg"
                    placeholder="Paste your invoice text here... For example:
Service national de la jeunesse 30.10.2024 29.11.2024 #101-3280 565,99 € 235 days 565,99 €"
                    disabled={isLoading}
                  />
                  <button
                    onClick={() => handleTextSubmit(textInput)}
                    className={`px-4 py-2 rounded font-medium transition-colors ${
                      isLoading || !textInput.trim() || textInput.trim().length < 5
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                    disabled={isLoading || !textInput.trim() || textInput.trim().length < 5}
                    title={
                      !textInput.trim() 
                        ? 'Please enter some text to parse' 
                        : textInput.trim().length < 5 
                        ? 'Text must be at least 5 characters long'
                        : 'Parse the entered text'
                    }
                  >
                    Parse Text
                  </button>
                </div>
              )}
              
              {isLoading && (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="mt-2 text-muted-foreground">
                    {activeTab === 'file' ? 'Processing file...' : 'Processing text...'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Email Template Manager */}
          

          {/* Email Template Editor */}
          {state.invoices.length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold">Email Template</h2>
                <Button
                  onClick={() => setIsHistoryOpen(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Archive className="w-4 h-4" />
                  View Archive ({state.archive.length})
                </Button>
              </div>
              <EmailTemplateEditor
                onPreview={handlePreviewTemplate}
                onOpenEmail={() => {}}
              />
            </div>
          )}

          {/* Analytics Dashboard */}
          {state.invoices.length > 0 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Analytics Dashboard</h2>
              <AnalyticsDashboard invoices={state.invoices} />
            </div>
          )}

          {/* Detailed Invoice Overview */}
          {state.invoices.length > 0 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Detailed Invoice Overview</h2>
              
              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800">Total Invoices</h3>
                  <p className="text-3xl font-bold text-blue-600">{state.invoices.length}</p>
                </div>
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800">Total Amount</h3>
                  <p className="text-3xl font-bold text-green-600">
                    {state.invoices.reduce((sum, inv) => sum + inv.amount, 0).toLocaleString('fr-FR', { 
                      style: 'currency', 
                      currency: 'EUR' 
                    })}
                  </p>
                </div>
                <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-orange-800">Overdue Invoices</h3>
                  <p className="text-3xl font-bold text-orange-600">
                    {state.invoices.filter(inv => {
                      const dueDate = new Date(inv.dueDate)
                      const today = new Date()
                      return dueDate < today
                    }).length}
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-red-800">Overdue Amount</h3>
                  <p className="text-3xl font-bold text-red-600">
                    {state.invoices
                      .filter(inv => {
                        const dueDate = new Date(inv.dueDate)
                        const today = new Date()
                        return dueDate < today
                      })
                      .reduce((sum, inv) => sum + inv.amount, 0)
                      .toLocaleString('fr-FR', { 
                        style: 'currency', 
                        currency: 'EUR' 
                      })}
                  </p>
                </div>
              </div>

              {/* Invoice List */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b">
                  <h3 className="text-lg font-semibold">Invoice Details</h3>
                </div>
                <div className="divide-y divide-gray-200">
                                  {state.invoices.map((inv, idx) => {
                  const dueDate = new Date(inv.dueDate)
                  const today = new Date()
                  const isOverdue = dueDate < today
                  const daysOverdue = isOverdue ? Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)) : 0
                  
                  return (
                    <div key={idx} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">{inv.client}</h4>
                              <p className="text-gray-600">Invoice #{inv.number}</p>
                              {inv.description && (
                                <p className="text-sm text-gray-500 mt-1">{inv.description}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-gray-900">
                                {inv.amount.toLocaleString('fr-FR', { 
                                  style: 'currency', 
                                  currency: inv.currency || 'EUR' 
                                })}
                              </p>
                              <p className={`text-sm ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                Due: {dueDate.toLocaleDateString('fr-FR')}
                                {isOverdue && ` (${daysOverdue} days overdue)`}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handlePreviewEmail(inv)}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                          >
                            Preview
                          </button>
                          <button
                            onClick={() => handleOpenEmail(inv)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
                          >
                            Open Email
                          </button>
                          <button
                            onClick={() => handleArchiveInvoice(inv)}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded transition-colors"
                          >
                            Archive
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Email Preview Dialog */}
        {previewInvoice && (
          <EmailPreview
            invoice={previewInvoice}
            template={state.emailTemplate}
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            onOpenEmail={() => {
              handleOpenEmail(previewInvoice)
              setIsPreviewOpen(false)
            }}
          />
        )}

        {/* Archive Dialog */}
        {archiveInvoiceData && (
          <ArchiveDialog
            invoice={archiveInvoiceData}
            template={state.emailTemplate}
            isOpen={isArchiveOpen}
            onClose={() => {
              setIsArchiveOpen(false)
              setArchiveInvoiceData(null)
            }}
            onArchive={handleArchiveConfirm}
          />
        )}

        {/* Archive History Dialog */}
        <ArchiveHistory
          archive={state.archive}
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          onRestore={restoreInvoice}
        />
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
    </div>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <InvoiceProvider>
      <AppContent />
    </InvoiceProvider>
  )
}

export default App 